import React, { useState, useEffect, createContext } from 'react';
import axios from 'axios';
import ApplicationError from './ApplicationError';
import Loading from './Loading';
import DonationForm from './DonationForm';
import GlobalPaymentsHPP from './GlobalPaymentsHPP';
import DonationProcessed from './DonationProcessed';

/**
 * The donation app context.
 * @type {React.Context<unknown>}
 */
export const DonationAppContext = createContext();

/**
 * Component that render the Donation form application.
 *
 * The component will render one child component based on the donationAppState
 * state variable.
 *
 * @returns {JSX.Element}
 * @component
 */
const DonationApp = () => {
  /**
   * Lookup object of all application states.
   * @type {{FORM_CONFIG_LOADED: number, DONATION_POST_PROCESSED: number, ERROR: number, BOOT: number, DONATION_PRE_PROCESSED: number}}
   */
  const APP_STATES = {

    /**
     * Application state when an error has occurred.
     */
    ERROR: 0,

    /**
     * Application state when the application has just been loaded.
     */
    BOOT: 1,

    /**
     * Application state when the form settings have been loaded and the form can be rendered.
     */
    FORM_CONFIG_LOADED: 2,

    /**
     * Application state when the form has been submitted, the GlobalPayments data has been generated and the
     * hosted payment page iframe can be rendered.
     */
    DONATION_PRE_PROCESSED: 3,

    /**
     * Application state when the donation has been post process by the API
     */
    DONATION_POST_PROCESSED: 4
  };

  /**
   * The donation application state.
   *
   * Contains the actual state, and data required for each state.
   */
  const [donationAppState, setDonationAppState] = useState({
    state: APP_STATES.BOOT,
    formConfig: {},
    donation: {},
    globalPaymentsResponse: {}
  });

  /**
   * Retrieves the form settings from the Settings endpoint.
   */
  const getFormConfig = () => {
    const settingsUrl = `${process.env.REACT_APP_HE_ECOM_API}/Preprocess/Settings`;

    axios.get(settingsUrl,
      {
        headers: {
          siteId: process.env.REACT_APP_HE_SITE_ID
        }
      })
      .then(response => {
        // Purely presentational setTimeout.
        setTimeout(() => {
          setDonationAppState(prevState => {
            return {
              ...prevState,
              state: APP_STATES.FORM_CONFIG_LOADED,
              formConfig: response.data
            };
          });
        }, 500);
      })
      .catch(() => {
        setDonationAppState(prevState => {
          return {
            ...prevState,
            state: APP_STATES.ERROR,
            error: (
              <>
                <p>There was a problem loading the form. Please try again later </p>
              </>
            )
          };
        });
      });
  };

  const getActiveComponent = () => {
    /**
     * Render active component based on application state.
     */
    switch (donationAppState.state) {
      case APP_STATES.BOOT:
        return (
          <Loading/>
        );

      case APP_STATES.FORM_CONFIG_LOADED:
        return (
          <DonationForm formConfig={donationAppState.formConfig}/>
        );

      case APP_STATES.DONATION_PRE_PROCESSED:
        return (
          <GlobalPaymentsHPP donation={donationAppState.donation}/>
        );

      case APP_STATES.DONATION_POST_PROCESSED:
        return (
          <DonationProcessed globalPaymentsResponse={donationAppState.globalPaymentsResponse}/>
        );

      case APP_STATES.ERROR:
      default:
        return (
          <ApplicationError error={donationAppState.error}/>
        );
    }
  };

  /**
   * Load the form settings on post render.
   */
  useEffect(() => {
    getFormConfig();
  }, []);

  return (
    <DonationAppContext.Provider value={{ APP_STATES: APP_STATES, setDonationAppState: setDonationAppState }}>
      { getActiveComponent() }
    </DonationAppContext.Provider>
  );
};

export default DonationApp;
